import { useEffect, useRef } from "react";
import { fetchMessages, turnResult } from "../lib/api";
import {
  pollTurn, loadPendingTurn, clearPendingTurn, repliesInHistory, mergeTurnAnswer, sameView, turnView, pollable,
  giveUpOutcome, randomId, PENDING_CLOCK_SLACK_MS,
} from "../lib/streamedTurn";
import { replyExtras } from "../lib/turnExtras";

// After a typed turn's wait ended while the server may still be making its
// file or picture: when its thread is read again for her answer (ms after
// giving up).
export const LATE_CHECKS_MS = [10000, 30000, 60000, 120000, 240000];
// The last of those reads couldn't load the thread (still offline): it is
// tried once more after this long — never taken for "her answer isn't there".
export const LATE_RETRY_MS = 30000;
// How many such turns are watched at once (the oldest is let go first): a
// new message sent meanwhile no longer stops the re-reads for the earlier
// file — it used to share the one pending slot with it.
export const LATE_MAX = 3;

// A typed turn whose reply hasn't reached the screen (lib/streamedTurn.js
// savePendingTurn), for pages/Luna.jsx:
// - resume(history): once a thread is on screen (`history`: its messages
//   when they were just loaded — read again otherwise), when the page comes
//   back into view and when it is back online, the turn left waiting in this
//   tab is picked up while its view is open: her answer from its thread when
//   it is there; otherwise — only while the server can still be on it
//   (pollable) — asked for under its key (pollTurn), the waiting bubble
//   counting from when it was sent. Past that only the thread is read:
//   asking under the key then would run (and bill) the turn again — and when
//   her answer isn't there, the toast says it may have failed and offers to
//   send it again. A thread that can't be read (still offline) says nothing
//   yet: the turn stays, for the next time.
// - settle(turn, e, kind): a typed turn's wait that ended without its reply
//   (lib/streamedTurn.js giveUpOutcome). "promise": the toast says its file
//   or picture turns up; "lost": that the connection went and her answer is
//   brought once it is back — both keep the turn, and its thread is read
//   again a few times (LATE_CHECKS_MS): loaded without her answer at the
//   last look, the "maybe" toast after all. "maybe": one look at the thread,
//   then a toast that it may have failed, offering to send it again (a
//   thread that can't be read: "lost" instead). Resolves true when handled
//   so; false for an ordinary failure (the caller's own error toast).
// One turn is asked for at a time, never next to another turn being
// answered; a late read waits until that turn is over. Her answer joins the
// screen only while its view (mode, Sohbet) is open — otherwise a toast says
// where it is — and never twice (lib/streamedTurn.js mergeTurnAnswer).
// A turn left waiting in a view that isn't open — a reload always opens the
// mode's own thread, so one asked in a Sohbet never was: nothing fetched,
// nothing said, and the deck was asked for again (client review, round 2) —
// gets one toast per turn saying where it is (notices.waitingElsewhere); its
// "Aç" opens that view, which picks it up. (One its late reads watch says
// so itself once they find it.)
//
// ctx, read afresh on every call: { view: {mode, conversationId} on screen,
// sending (a turn being answered in this tab), lang, mood (today's, or null),
// setMessages, beginTurn({ view, making, startedAt, stillWorking }), endTurn(),
// setStillWorking(on), setConnected(ok), notices: { stillMaking(kind),
// connectionLost(kind), maybeFailed(turn), readyElsewhere(view, kind,
// { turn, answers }), waitingElsewhere(view, kind, { turn, pending }),
// failed(e) } } — pending: it can still be asked for under its key
// (pollable), else only its thread is read once that view is open.
export function usePendingTurn(ctx) {
  const live = useRef(ctx);
  live.current = ctx;
  const busy = useRef(null);       // the key of the turn being picked up
  const late = useRef(new Map());  // key -> { turn, timers, due, final, retried }
  const told = useRef(new Set());  // keys whose "it is in that view" toast went
  const self = useRef(null);

  const here = (turn) => sameView(live.current.view, turnView(turn));

  const stopLate = (key) => {
    const entry = late.current.get(key);
    if (!entry) return;
    entry.timers.forEach(clearTimeout);
    late.current.delete(key);
  };

  // Her answer(s) to `turn` in its thread (`history` when that is loaded
  // already, else fetched): on screen when its view is open, else a toast
  // that it is ready there. true when found; false when the thread loaded
  // without them; null when it couldn't be read (offline, the server down).
  const readThread = async (turn, history = null) => {
    let thread = history;
    if (!Array.isArray(thread)) {
      try {
        thread = await fetchMessages(turn.mode, turn.conversationId);
      } catch (_) {
        return null;
      }
    }
    const answers = repliesInHistory(thread, turn.text, { since: turn.at - PENDING_CLOCK_SLACK_MS });
    if (!answers?.length) return false;
    clearPendingTurn(turn.key);
    stopLate(turn.key);
    if (here(turn)) live.current.setMessages((m) => mergeTurnAnswer(m, turn, answers));
    else live.current.notices.readyElsewhere(turnView(turn), turn.making, { turn, answers });
    return true;
  };

  const checkLate = async (key, final) => {
    const entry = late.current.get(key);
    if (!entry) return;
    // (her late answer must not land in the middle of another turn: it is
    // read once that one is over — the effect below)
    if (live.current.sending || busy.current) {
      entry.due = true;
      entry.final = entry.final || final;
      return;
    }
    entry.due = false;
    const found = await readThread(entry.turn);
    if (late.current.get(key) !== entry || !(final || entry.final)) return;
    // The thread loaded without her answer at the last look: the promise is
    // taken back — it may have failed after all, and sending it again is
    // offered.
    if (found === false) {
      stopLate(key);
      clearPendingTurn(key);
      live.current.notices.maybeFailed(entry.turn);
      return;
    }
    // It couldn't be read at all (still offline): once more a little later;
    // then the watch ends quietly — the turn stays in this tab, and the page
    // back in view or back online looks for it again (resume).
    if (found === null) {
      if (entry.retried) {
        stopLate(key);
        return;
      }
      entry.retried = true;
      entry.timers.push(setTimeout(() => checkLate(key, true), LATE_RETRY_MS));
    }
  };

  // The late reads put off while a turn was being answered or picked up.
  const runDue = () => {
    if (live.current.sending || busy.current) return;
    for (const [key, entry] of [...late.current]) {
      if (entry.due) checkLate(key, entry.final);
    }
  };

  const watchLate = (turn) => {
    stopLate(turn.key);
    const entry = { turn, due: false, final: false, retried: false, timers: [] };
    entry.timers = LATE_CHECKS_MS.map((ms, i) => setTimeout(() => checkLate(turn.key, i === LATE_CHECKS_MS.length - 1), ms));
    late.current.set(turn.key, entry);
    while (late.current.size > LATE_MAX) stopLate(late.current.keys().next().value);
  };

  const settle = async (turn, e, kind) => {
    const outcome = giveUpOutcome(e, kind);
    if (outcome === "promise" || outcome === "lost") {
      if (outcome === "promise") live.current.notices.stillMaking(kind);
      else live.current.notices.connectionLost(kind);
      watchLate({ ...turn, making: kind });
      return true;
    }
    if (outcome === "maybe") {
      const found = await readThread(turn);
      if (found === false) {
        clearPendingTurn(turn.key);
        live.current.notices.maybeFailed({ ...turn, making: kind });
      } else if (found === null) {
        // (its thread can't be read either: the connection is what went)
        live.current.notices.connectionLost(kind);
        watchLate({ ...turn, making: kind });
      }
      return true;
    }
    return false;
  };

  // A turn left waiting in a view that isn't open: said once (see above).
  const tellElsewhere = (turn) => {
    if (told.current.has(turn.key) || late.current.has(turn.key)) return;
    told.current.add(turn.key);
    live.current.notices.waitingElsewhere(turnView(turn), turn.making, { turn, pending: pollable(turn) });
  };

  const pickUp = async (turn, { poll, history = null }) => {
    if (busy.current || live.current.sending) return;
    if (!here(turn)) {
      tellElsewhere(turn);
      return;
    }
    busy.current = turn.key;
    try {
      const found = await readThread(turn, history);
      if (found) return;
      if (!poll || !pollable(turn)) {
        // Past the server's deadline and not in its thread (nor still being
        // looked for — its late reads): it most likely failed. (A thread
        // that couldn't be read says nothing yet: offline still.)
        if (found === false && !pollable(turn) && !late.current.has(turn.key)) {
          clearPendingTurn(turn.key);
          live.current.notices.maybeFailed(turn);
        }
        return;
      }
      if (live.current.sending || !here(turn)) return;
      const { lang, mood, setMessages, beginTurn } = live.current;
      setMessages((m) => mergeTurnAnswer(m, turn, []));
      beginTurn({ view: turnView(turn), making: turn.making, startedAt: turn.at, stillWorking: true });
      try {
        const result = turnResult(await pollTurn(
          { message: turn.text, mode: turn.mode, lang, conversationId: turn.conversationId, mood, full: true },
          {
            idempotencyKey: turn.key, typed: true, since: turn.at, accepted: turn.accepted === true,
            onStillWorking: () => live.current.setStillWorking(true),
            onConnection: (ok) => live.current.setConnected(ok),
          }));
        clearPendingTurn(turn.key);
        // (a late watch left by an earlier wait ends too: read later, it
        // would toast "ready there" for an answer already on screen)
        stopLate(turn.key);
        const answer = {
          id: `l-${randomId()}`, role: "luna", text: result.reply, mode: turn.mode, messageId: result.messageId,
          ...(result.safety ? { safety: result.safety } : {}), ...replyExtras(result),
        };
        if (here(turn)) live.current.setMessages((m) => mergeTurnAnswer(m, turn, [answer]));
        else live.current.notices.readyElsewhere(turnView(turn), turn.making, { turn, answers: [answer] });
      } catch (e) {
        if (!(await settle(turn, e, turn.making))) {
          clearPendingTurn(turn.key);
          live.current.notices.failed(e);
        }
      } finally {
        live.current.endTurn();
      }
    } finally {
      busy.current = null;
      runDue();
    }
  };

  const resume = (history = null) => {
    const turn = loadPendingTurn();
    if (turn) pickUp(turn, { poll: pollable(turn), history });
  };

  self.current = { resume, runDue };

  // ...when the page comes back into view, and when it is back online (the
  // turn's own wait, if it is still going, carries on by itself: nothing to
  // pick up then).
  useEffect(() => {
    const onShow = () => {
      if (document.visibilityState === "visible") self.current.resume();
    };
    const onOnline = () => self.current.resume();
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onShow);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  // ...and once that turn is over.
  useEffect(() => {
    if (!ctx.sending) self.current.runDue();
  }, [ctx.sending]);

  useEffect(() => {
    const watches = late;
    return () => {
      watches.current.forEach((entry) => entry.timers.forEach(clearTimeout));
      watches.current.clear();
    };
  }, []);

  return { resume, settle, watchLate };
}
